import React, { useEffect, useMemo, useState } from "react";
import { api, fmtINR, fmtNum, todayISO } from "../lib/api";
import { Trash2, ShoppingCart, Receipt, ArrowLeft, UserPlus, Edit3, X, Save } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import BillShareModal from "../components/BillShareModal";

const CAT_LABEL = { chicken: "Chicken", mutton: "Mutton", offals: "Offals" };

export default function Wholesale() {
  const [products, setProducts] = useState([]);
  const [dailyPrices, setDailyPrices] = useState({});
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [customerMultipliers, setCustomerMultipliers] = useState({});
  const [paperRates, setPaperRates] = useState({});
  const [customerRates, setCustomerRates] = useState({});
  const [activeCat, setActiveCat] = useState("chicken");
  const [cart, setCart] = useState([]);
  const [paid, setPaid] = useState("");
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newCustName, setNewCustName] = useState("");
  const [newCustPhone, setNewCustPhone] = useState("");
  const [lastSale, setLastSale] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [rateEditorOpen, setRateEditorOpen] = useState(false);
  const [rateDraft, setRateDraft] = useState({});

  const loadInitial = async () => {
    const [pRes, prRes, cRes, paperRes] = await Promise.all([
      api.get("/products?active_only=true"),
      api.get(`/daily-prices?date=${todayISO()}&channel=wholesale`),
      api.get("/customers"),
      api.get(`/paper-rates?date=${todayISO()}`),
    ]);
    setProducts(pRes.data);
    const map = {};
    prRes.data.forEach((p) => (map[p.product_id] = p.price_per_kg));
    setDailyPrices(map);
    setCustomers(cRes.data);
    const pMap = {};
    paperRes.data.forEach((p) => (pMap[p.category] = p.rate_per_kg));
    setPaperRates(pMap);
  };

  const loadCustomerRates = async (cid) => {
    if (!cid) {
      setCustomerRates({});
      setCustomerMultipliers({});
      return;
    }
    const [rRes, mRes] = await Promise.all([
      api.get(`/customer-rates?customer_id=${cid}`),
      api.get(`/customer-multipliers?customer_id=${cid}`),
    ]);
    const map = {};
    rRes.data.forEach((r) => (map[r.product_id] = r.price_per_kg));
    setCustomerRates(map);
    const mm = {};
    mRes.data.forEach((m) => (mm[m.product_id] = m.multiplier));
    setCustomerMultipliers(mm);
  };

  useEffect(() => {
    loadInitial();
  }, []);

  useEffect(() => {
    loadCustomerRates(customerId);
  }, [customerId]);

  // Rate priority: customer saved rate > paper rate × multiplier > daily wholesale rate
  const getRate = (productId) => {
    if (customerRates[productId]) return customerRates[productId];
    const product = products.find((p) => p.id === productId);
    const mult = customerMultipliers[productId];
    const paper = product ? paperRates[product.category] : 0;
    if (mult && paper) return Math.round(parseFloat(mult) * paper);
    return dailyPrices[productId] || 0;
  };

  const getRateSource = (productId) => {
    if (customerRates[productId]) return "saved";
    const product = products.find((p) => p.id === productId);
    const mult = customerMultipliers[productId];
    const paper = product ? paperRates[product.category] : 0;
    if (mult && paper) return "multiplier";
    if (dailyPrices[productId]) return "daily";
    return "none";
  };

  const cats = useMemo(() => {
    const out = {};
    products.forEach((p) => {
      if (!out[p.category]) out[p.category] = [];
      out[p.category].push(p);
    });
    return out;
  }, [products]);

  const addToCart = (product) => {
    if (!customerId) {
      toast.error("Select a customer first");
      return;
    }
    const rate = getRate(product.id);
    const unit = product.unit || "kg";
    setCart((c) => [
      ...c,
      {
        line_id: Math.random().toString(36).slice(2),
        product_id: product.id,
        product_name: product.name,
        category: product.category,
        unit,
        rate,
        kg: 1,
        amount: rate * 1,
      },
    ]);
  };

  const updateLine = (line_id, field, val) => {
    setCart((c) =>
      c.map((it) => {
        if (it.line_id !== line_id) return it;
        const num = parseFloat(val) || 0;
        if (field === "kg") {
          const q = it.unit === "piece" ? Math.round(num) : num;
          return { ...it, kg: q, amount: +(q * it.rate).toFixed(2) };
        }
        if (field === "amount") {
          let kg = it.rate > 0 ? num / it.rate : 0;
          if (it.unit === "piece") kg = Math.round(kg);
          kg = +kg.toFixed(it.unit === "piece" ? 0 : 3);
          return { ...it, amount: num, kg };
        }
        if (field === "rate") {
          return { ...it, rate: num, amount: +(it.kg * num).toFixed(2) };
        }
        return it;
      })
    );
  };

  const removeLine = (line_id) => setCart((c) => c.filter((it) => it.line_id !== line_id));

  const totals = useMemo(() => {
    const totalKg = cart
      .filter((it) => (it.unit || "kg") !== "piece")
      .reduce((s, it) => s + (it.kg || 0), 0);
    const totalPcs = cart
      .filter((it) => it.unit === "piece")
      .reduce((s, it) => s + (it.kg || 0), 0);
    const totalAmount = Math.round(cart.reduce((s, it) => s + (it.amount || 0), 0));
    return { totalKg, totalPcs, totalAmount };
  }, [cart]);

  const saveCustomerRates = async () => {
    if (!customerId || cart.length === 0) return;
    const items = cart.map((it) => ({
      customer_id: customerId,
      product_id: it.product_id,
      price_per_kg: it.rate,
    }));
    await api.post("/customer-rates/bulk", items);
    toast.success("Customer rates saved");
    await loadCustomerRates(customerId);
  };

  // Open dedicated rate editor for ALL products of selected customer
  const openRateEditor = () => {
    if (!customerId) {
      toast.error("Select a customer first");
      return;
    }
    const draft = {};
    products.forEach((p) => {
      draft[p.id] = customerRates[p.id] ?? "";
    });
    setRateDraft(draft);
    setRateEditorOpen(true);
  };

  const saveRateEditor = async () => {
    const items = Object.entries(rateDraft)
      .filter(([_, v]) => v !== "" && parseFloat(v) > 0)
      .map(([product_id, v]) => ({
        customer_id: customerId,
        product_id,
        price_per_kg: parseFloat(v),
      }));
    if (items.length === 0) {
      toast.error("Enter at least one rate");
      return;
    }
    await api.post("/customer-rates/bulk", items);
    toast.success(`Saved ${items.length} rates for this customer`);
    await loadCustomerRates(customerId);
    setRateEditorOpen(false);
  };

  // Quick remove a saved customer rate (revert to daily wholesale)
  const clearCustomerRate = (productId) => {
    setRateDraft((d) => ({ ...d, [productId]: "" }));
  };

  const checkout = async () => {
    if (!customerId) {
      toast.error("Select a customer");
      return;
    }
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    const customer = customers.find((c) => c.id === customerId);
    try {
      const res = await api.post("/sales", {
        date: todayISO(),
        channel: "wholesale",
        customer_id: customerId,
        customer_name: customer?.name || "",
        items: cart.map((it) => ({
          product_id: it.product_id,
          product_name: it.product_name,
          category: it.category,
          unit: it.unit || "kg",
          kg: it.kg,
          rate: it.rate,
          amount: it.amount,
        })),
        payment_mode: "credit",
        paid_amount: parseFloat(paid) || 0,
      });
      toast.success(`Order saved · ${fmtINR(totals.totalAmount)}`);
      setLastSale({ ...res.data, _customer_phone: customer?.phone });
      setShareOpen(true);
      setCart([]);
      setPaid("");
    } catch (e) {
      toast.error("Failed to save order");
    }
  };

  const createCustomer = async () => {
    if (!newCustName.trim()) return;
    const res = await api.post("/customers", { name: newCustName, phone: newCustPhone });
    setCustomers((c) => [...c, res.data]);
    setCustomerId(res.data.id);
    setNewCustOpen(false);
    setNewCustName("");
    setNewCustPhone("");
    toast.success("Customer added");
  };

  return (
    <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-3.5rem)] lg:max-h-screen lg:overflow-hidden bg-zinc-50" data-testid="ws-page">
      <div className="flex-1 lg:overflow-y-auto p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="border border-zinc-300 px-3 py-2 text-xs uppercase tracking-wider hover:bg-zinc-100"
              data-testid="ws-back-btn"
            >
              <ArrowLeft className="h-4 w-4 inline mr-1" />
              Back
            </Link>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                B2B Orders
              </div>
              <h1 className="font-display text-3xl font-bold tracking-tight">
                Wholesale
              </h1>
            </div>
          </div>
        </div>

        {/* Customer selector */}
        <div className="border border-zinc-200 bg-white p-4 mb-5 flex items-center gap-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 min-w-fit">
            Customer
          </div>
          <select
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            data-testid="ws-customer-select"
            className="flex-1 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
          >
            <option value="">— Select a customer —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.phone ? `· ${c.phone}` : ""}
              </option>
            ))}
          </select>
          <button
            onClick={() => setNewCustOpen(true)}
            data-testid="ws-new-customer-btn"
            className="border border-zinc-300 px-3 py-2 text-xs uppercase tracking-wider hover:bg-zinc-100 flex items-center gap-1"
          >
            <UserPlus className="h-4 w-4" />
            New
          </button>
          <button
            onClick={openRateEditor}
            disabled={!customerId}
            data-testid="ws-edit-rates-btn"
            className="bg-sky-600 text-white px-3 py-2 text-xs uppercase tracking-wider hover:bg-sky-700 disabled:bg-zinc-300 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <Edit3 className="h-4 w-4" />
            Edit Rates
          </button>
        </div>

        {newCustOpen && (
          <div className="border border-zinc-300 bg-white p-4 mb-5 space-y-2" data-testid="ws-new-customer-form">
            <input
              placeholder="Customer name"
              value={newCustName}
              onChange={(e) => setNewCustName(e.target.value)}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
              data-testid="ws-new-name"
            />
            <input
              placeholder="Phone (optional)"
              value={newCustPhone}
              onChange={(e) => setNewCustPhone(e.target.value)}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
              data-testid="ws-new-phone"
            />
            <div className="flex gap-2">
              <button
                onClick={createCustomer}
                data-testid="ws-new-save"
                className="bg-zinc-900 text-white px-4 py-2 text-xs uppercase tracking-wider hover:bg-zinc-800"
              >
                Save
              </button>
              <button
                onClick={() => setNewCustOpen(false)}
                className="border border-zinc-300 px-4 py-2 text-xs uppercase tracking-wider hover:bg-zinc-100"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Category tabs */}
        <div className="flex gap-0 border-b border-zinc-200 mb-5">
          {Object.keys(cats).map((c) => (
            <button
              key={c}
              onClick={() => setActiveCat(c)}
              data-testid={`ws-tab-${c}`}
              className={`px-5 py-3 text-sm font-medium uppercase tracking-wider border-b-2 transition-colors ${
                activeCat === c
                  ? "border-sky-600 text-zinc-950"
                  : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
            >
              {CAT_LABEL[c] || c}
            </button>
          ))}
        </div>

        {/* Product grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {(cats[activeCat] || []).map((p) => {
            const rate = getRate(p.id);
            const src = getRateSource(p.id);
            const unit = p.unit || "kg";
            const srcLabel = {
              saved: "Saved",
              multiplier: `${customerMultipliers[p.id]}× Paper`,
              daily: `/${unit === "piece" ? "pc" : "kg"}`,
              none: "—",
            }[src];
            return (
              <button
                key={p.id}
                onClick={() => addToCart(p)}
                disabled={!rate}
                data-testid={`ws-product-${p.id}`}
                className={`text-left border bg-white p-4 min-h-[120px] flex flex-col justify-between transition-all ${
                  rate
                    ? "border-zinc-200 hover:border-zinc-900 hover:shadow-[2px_2px_0_0_#18181b] active:scale-[0.98]"
                    : "border-zinc-200 opacity-50 cursor-not-allowed"
                }`}
              >
                <div className="text-sm font-semibold leading-tight">{p.name}</div>
                <div className="mt-3 flex items-end justify-between">
                  <div className="font-mono-num font-bold text-xl">
                    {rate ? `₹${fmtNum(rate, 0)}` : "—"}
                  </div>
                  <div className={`text-[10px] uppercase tracking-wider ${
                    src === "saved"
                      ? "text-emerald-600 font-bold"
                      : src === "multiplier"
                      ? "text-sky-600 font-bold"
                      : "text-zinc-500"
                  }`}>
                    {srcLabel}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Cart pane */}
      <aside className="w-full lg:w-[420px] xl:w-[460px] border-t lg:border-t-0 lg:border-l border-zinc-200 bg-white flex flex-col lg:h-full">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-zinc-900" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Order
              </div>
              <div className="font-display font-bold text-lg">
                {cart.length} items
              </div>
            </div>
          </div>
          {cart.length > 0 && (
            <button
              onClick={saveCustomerRates}
              data-testid="ws-save-rates"
              className="text-xs uppercase tracking-wider text-zinc-700 hover:text-sky-600 border border-zinc-300 px-2 py-1"
            >
              Save Rates
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {cart.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">
              {customerId ? "Tap a product to add it" : "Select a customer first"}
            </div>
          ) : (
            cart.map((it) => (
              <div
                key={it.line_id}
                className="border-b border-zinc-200 px-5 py-4"
                data-testid={`ws-line-${it.product_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="text-sm font-semibold">{it.product_name}</div>
                  <button
                    onClick={() => removeLine(it.line_id)}
                    className="text-zinc-400 hover:text-sky-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                      Rate ₹/{it.unit === "piece" ? "pc" : "kg"}
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={it.rate}
                      onChange={(e) => updateLine(it.line_id, "rate", e.target.value)}
                      data-testid={`ws-line-rate-${it.product_id}`}
                      className="w-full border border-zinc-300 px-2 py-1.5 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                      {it.unit === "piece" ? "Pcs" : "Kg"}
                    </div>
                    <input
                      type="number"
                      step={it.unit === "piece" ? "1" : "0.001"}
                      value={it.kg}
                      onChange={(e) => updateLine(it.line_id, "kg", e.target.value)}
                      data-testid={`ws-line-kg-${it.product_id}`}
                      className="w-full border border-zinc-300 px-2 py-1.5 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                      Amount ₹
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={it.amount}
                      onChange={(e) => updateLine(it.line_id, "amount", e.target.value)}
                      data-testid={`ws-line-amount-${it.product_id}`}
                      className="w-full border border-zinc-300 px-2 py-1.5 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="border-t border-zinc-200 px-5 py-4 space-y-3 bg-zinc-50">
          {totals.totalKg > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-zinc-600">Total Kg</span>
              <span className="font-mono-num font-semibold" data-testid="ws-total-kg">
                {fmtNum(totals.totalKg, 3)} kg
              </span>
            </div>
          )}
          {totals.totalPcs > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-zinc-600">Total Pieces</span>
              <span className="font-mono-num font-semibold" data-testid="ws-total-pcs">
                {Math.round(totals.totalPcs)} pcs
              </span>
            </div>
          )}
          <div className="flex justify-between items-baseline">
            <span className="text-xs uppercase tracking-wider text-zinc-500">
              Grand Total
            </span>
            <span className="font-display font-bold text-3xl font-mono-num" data-testid="ws-total-amount">
              {fmtINR(totals.totalAmount)}
            </span>
          </div>
          <input
            type="number"
            placeholder="Paid amount (0 = credit)"
            value={paid}
            onChange={(e) => setPaid(e.target.value)}
            data-testid="ws-paid-input"
            className="w-full border border-zinc-300 px-3 py-2 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
          />
          <button
            onClick={checkout}
            disabled={cart.length === 0 || !customerId}
            data-testid="ws-checkout-btn"
            className="w-full bg-zinc-900 text-white py-4 font-display font-bold text-lg uppercase tracking-wider hover:bg-zinc-800 active:scale-[0.98] transition-all disabled:bg-zinc-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Receipt className="h-5 w-5" />
            Save Order
          </button>
        </div>
      </aside>
      <BillShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        sale={lastSale}
        defaultPhone={lastSale?._customer_phone || ""}
      />

      {/* Per-customer Rate Editor Modal */}
      {rateEditorOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setRateEditorOpen(false)}
          data-testid="rate-editor-modal"
        >
          <div
            className="bg-white border border-zinc-900 max-w-2xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-zinc-200">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Custom Rates for
                </div>
                <div className="font-display text-xl font-bold">
                  {customers.find((c) => c.id === customerId)?.name}
                </div>
                <div className="text-xs text-zinc-600 mt-1">
                  Leave blank → uses today&apos;s wholesale rate. Filled → overrides.
                </div>
              </div>
              <button
                onClick={() => setRateEditorOpen(false)}
                className="text-zinc-400 hover:text-zinc-900"
                data-testid="rate-editor-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-1">
              {["chicken", "mutton", "offals"].map((cat) => {
                const prods = products.filter((p) => p.category === cat);
                if (prods.length === 0) return null;
                return (
                  <div key={cat} className="mb-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                      {cat}
                    </div>
                    <div className="border border-zinc-200">
                      {prods.map((p) => {
                        const u = p.unit === "piece" ? "pc" : "kg";
                        const dailyR = dailyPrices[p.id] || 0;
                        return (
                          <div
                            key={p.id}
                            className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-100 last:border-b-0"
                          >
                            <div className="flex-1">
                              <div className="text-sm font-medium">{p.name}</div>
                              <div className="text-[10px] text-zinc-500">
                                Today&apos;s wholesale: {dailyR ? `₹${dailyR}/${u}` : "—"}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-zinc-500 text-sm">₹</span>
                              <input
                                type="number"
                                step="0.01"
                                value={rateDraft[p.id] ?? ""}
                                onChange={(e) =>
                                  setRateDraft((d) => ({ ...d, [p.id]: e.target.value }))
                                }
                                data-testid={`rate-editor-input-${p.id}`}
                                placeholder={dailyR ? String(dailyR) : "0"}
                                className="w-24 border border-zinc-300 px-2 py-1.5 font-mono-num text-right text-base focus:outline-none focus:ring-2 focus:ring-sky-600"
                              />
                              <span className="text-[10px] uppercase tracking-wider text-zinc-500 w-6">
                                /{u}
                              </span>
                              {rateDraft[p.id] && (
                                <button
                                  onClick={() => clearCustomerRate(p.id)}
                                  className="text-zinc-300 hover:text-rose-600"
                                  title="Clear (use daily rate)"
                                  data-testid={`rate-editor-clear-${p.id}`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 p-5 border-t border-zinc-200 bg-zinc-50">
              <button
                onClick={() => setRateEditorOpen(false)}
                className="flex-1 border border-zinc-300 px-4 py-3 text-xs uppercase tracking-wider hover:bg-zinc-100"
              >
                Cancel
              </button>
              <button
                onClick={saveRateEditor}
                data-testid="rate-editor-save"
                className="flex-1 bg-sky-600 text-white px-4 py-3 text-xs uppercase tracking-wider hover:bg-sky-700 flex items-center justify-center gap-2"
              >
                <Save className="h-4 w-4" />
                Save Rates
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
